// SessionShell — Mode-aware session wrapper
//
// Renders SessionHeader, selects the correct layout based on teachingForm,
// creates ChatPanel with accent color, and routes concept map data to
// DialogLayout or tool results to ContentPanel-based layouts.

import { useCallback } from 'react';
import type { TeachingForm, BloomLevel } from '../../types';
import type { ChatMessage, ToolResult, BloomUpdate } from '@hooks/useAnnaChat';
import type { VoiceAvailabilityState } from '@hooks/useVoiceAvailability';
import type { VoiceInputState } from '@hooks/useVoiceInput';
import type { Node, Edge } from '@xyflow/react';
import SessionHeader from './SessionHeader';
import ChatPanel from './ChatPanel';
import IntroductionLayout from '../layouts/IntroductionLayout';
import DialogLayout from '../layouts/DialogLayout';
import ExamLayout from '../layouts/ExamLayout';

interface SessionShellProps {
  teachingForm: TeachingForm;
  subtopicTitle: string;
  messages: ChatMessage[];
  toolResults: ToolResult[];
  bloomUpdates: BloomUpdate[];
  isStreaming: boolean;
  error: string | null;
  sendMessage: (text: string) => Promise<void>;
  clearError: () => void;
  onEnd: () => void;
  // Concept map (from useConceptMap in SessionPage)
  conceptMapNodes: Node[];
  conceptMapEdges: Edge[];
  conceptMapLoading: boolean;
  onNodeDragStop: (nodeId: string, position: { x: number; y: number }) => void;
  currentBloomLevel: BloomLevel;
  totalSessions?: number;
  // Voice props (threaded from SessionPage hooks)
  voiceAvailability?: VoiceAvailabilityState;
  voiceState?: VoiceInputState;
  voiceError?: string | null;
  audioLevel?: number;
  interimTranscript?: string;
  onVoiceToggle?: () => void;
  voiceOutputEnabled?: boolean;
  isAnnaSpeaking?: boolean;
  onVoiceOutputToggle?: () => void;
  /** Concept map node click: pre-fill chat input (dialog mode only) */
  onNodeClick?: (nodeId: string, nodeLabel: string) => void;
  prefillText?: string;
  onPrefillConsumed?: () => void;
}

const COLOR_MAP: Record<TeachingForm, string> = {
  introduction: 'amber',
  dialog: 'blue',
  exam: 'indigo',
};

const BG_MAP: Record<TeachingForm, string> = {
  introduction: 'bg-amber-950/30',
  dialog: 'bg-blue-950/30',
  exam: 'bg-indigo-950/30',
};

export default function SessionShell({
  teachingForm,
  subtopicTitle,
  messages,
  toolResults,
  bloomUpdates,
  isStreaming,
  error,
  sendMessage,
  clearError,
  onEnd,
  conceptMapNodes,
  conceptMapEdges,
  conceptMapLoading,
  onNodeDragStop,
  currentBloomLevel,
  totalSessions = 0,
  voiceAvailability,
  voiceState,
  voiceError,
  audioLevel,
  interimTranscript,
  onVoiceToggle,
  voiceOutputEnabled,
  isAnnaSpeaking,
  onVoiceOutputToggle,
  onNodeClick,
  prefillText,
  onPrefillConsumed,
}: SessionShellProps) {
  const accentColor = COLOR_MAP[teachingForm];

  // Callback for Exercise explain_in_own_words → send as chat message
  const handleSubmitAnswer = useCallback(
    (answer: string) => {
      sendMessage(answer);
    },
    [sendMessage],
  );

  const chatPanel = (
    <ChatPanel
      messages={messages}
      isStreaming={isStreaming}
      error={error}
      onSendMessage={sendMessage}
      onClearError={clearError}
      accentColor={accentColor}
      voiceAvailability={voiceAvailability}
      voiceState={voiceState}
      voiceError={voiceError}
      audioLevel={audioLevel}
      interimTranscript={interimTranscript}
      onVoiceToggle={onVoiceToggle}
      voiceOutputEnabled={voiceOutputEnabled}
      isAnnaSpeaking={isAnnaSpeaking}
      onVoiceOutputToggle={onVoiceOutputToggle}
      prefillText={prefillText}
      onPrefillConsumed={onPrefillConsumed}
    />
  );

  const renderLayout = () => {
    switch (teachingForm) {
      case 'introduction':
        return (
          <IntroductionLayout
            chatPanel={chatPanel}
            toolResults={toolResults}
            accentColor={accentColor}
            isStreaming={isStreaming}
            onSubmitAnswer={handleSubmitAnswer}
          />
        );
      case 'dialog':
        return (
          <DialogLayout
            chatPanel={chatPanel}
            conceptMapNodes={conceptMapNodes}
            conceptMapEdges={conceptMapEdges}
            conceptMapLoading={conceptMapLoading}
            onNodeDragStop={onNodeDragStop}
            currentBloomLevel={currentBloomLevel}
            totalSessions={totalSessions}
            lastTeachingForm={teachingForm}
            readOnly={false}
            onNodeClick={onNodeClick}
          />
        );
      case 'exam':
        return (
          <ExamLayout
            chatPanel={chatPanel}
            toolResults={toolResults}
            accentColor={accentColor}
            isStreaming={isStreaming}
            onSubmitAnswer={handleSubmitAnswer}
          />
        );
    }
  };

  return (
    <div className={`flex flex-col h-dvh ${BG_MAP[teachingForm]}`}>
      <SessionHeader
        subtopicTitle={subtopicTitle}
        teachingForm={teachingForm}
        bloomUpdates={bloomUpdates}
        accentColor={accentColor}
        onEnd={onEnd}
      />
      <div className="flex-1 min-h-0">
        {renderLayout()}
      </div>
    </div>
  );
}
